import { useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import MovieIcon from '@mui/icons-material/MovieFilterOutlined';
import AgentIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import EditIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import ViewInArIcon from '@mui/icons-material/ViewInArOutlined';
import FaceIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import PortraitIcon from '@mui/icons-material/PortraitOutlined';
import CameraAltIcon from '@mui/icons-material/CameraAltOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import DocsIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import CloseIcon from '@mui/icons-material/CloseRounded';
import TokenIcon from '@mui/icons-material/TokenOutlined';
import PaymentIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import RecoveryIcon from '@mui/icons-material/MarkEmailReadOutlined';
import KeyIcon from '@mui/icons-material/VpnKeyOutlined';
import SupportIcon from '@mui/icons-material/SupportAgentOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import { useAuth } from '../auth/AuthProvider';
import LocaleSwitcher from '../i18n/LocaleSwitcher';
import { useI18n, useTranslation } from '../i18n';
import { format } from '../i18n/I18nProvider';
import { useCreditsBalance, formatCredits } from '../hooks/useCredits';
import { SignupNudgeProvider, useSignupNudge } from './SignupNudge';
import { PROMO_CREDITS } from '../lib/promo';
import { AgentJobsIndicator } from './agent/AgentJobsIndicator';
import { NewVersionNotice } from './NewVersionNotice';
import { BrandMark } from './BrandMark';
// The public API reference (docs.<brand domain>, served from this app's /api-docs/).
import { apiDocsUrl } from '../lib/brand';

const DRAWER_WIDTH = 232;

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const t = useTranslation();
  const { locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { credits } = useCreditsBalance({ pollMs: 30_000 });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const featureItems = [
    { to: '/', label: 'Home', icon: <HomeIcon fontSize="small" /> },
    { to: '/agent', label: t.videoAgent.navTitle, icon: <AgentIcon fontSize="small" /> },
    { to: '/minimax-h3', label: t.minimaxH3.navTitle, icon: <MovieIcon fontSize="small" /> },
    { to: '/image-edit', label: t.imageEdit.navTitle, icon: <EditIcon fontSize="small" /> },
    { to: '/multi-angle', label: t.multiAngle.navTitle, icon: <ViewInArIcon fontSize="small" /> },
    { to: '/face-swap', label: t.faceSwap.navTitle, icon: <FaceIcon fontSize="small" /> },
    { to: '/text-to-image', label: t.textToImage.navTitle, icon: <ImageIcon fontSize="small" /> },
    { to: '/human-portrait', label: t.humanPortrait.navTitle, icon: <PortraitIcon fontSize="small" /> },
    { to: '/instagram-generator', label: t.instagramGenerator.navTitle, icon: <CameraAltIcon fontSize="small" /> },
    { to: '/history', label: t.history.navTitle, icon: <HistoryIcon fontSize="small" /> },
  ];

  const accountItems = [
    { to: '/credits', label: t.credits.navTitle, icon: <TokenIcon fontSize="small" /> },
    { to: '/payment', label: t.payment.navTitle, icon: <PaymentIcon fontSize="small" /> },
    { to: '/account/password', label: t.changePassword.navTitle, icon: <LockIcon fontSize="small" /> },
    { to: '/account/recovery', label: t.recoveryEmail.navTitle, icon: <RecoveryIcon fontSize="small" /> },
    { to: '/account/api-keys', label: t.apiKeys.navTitle, icon: <KeyIcon fontSize="small" /> },
    { to: '/support', label: t.support.navTitle, icon: <SupportIcon fontSize="small" /> },
  ];

  const isGlobalAdmin = user?.role === 'global_admin';

  const initial = user?.name?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? '?';

  const closeMenu = () => setMenuAnchor(null);
  const goTo = (to: string) => {
    closeMenu();
    navigate(to);
  };

  return (
    <SignupNudgeProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ px: 3, py: 2.5 }}>
          <BrandMark size={18} />
        </Box>

        <List sx={{ px: 1, py: 0.5, flex: 1 }}>
          {featureItems.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={location.pathname === item.to}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ mx: 2 }} />
        <List sx={{ px: 1, py: 0.5 }}>
          {accountItems.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={location.pathname === item.to}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
          <ListItemButton component="a" href={apiDocsUrl(locale)} target="_blank" rel="noopener">
            <ListItemIcon>
              <DocsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t.apiKeys.docsNav} />
            <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </ListItemButton>
        </List>

        {isGlobalAdmin && (
          <>
            <Divider sx={{ mx: 2 }} />
            <List sx={{ px: 1, py: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to="/admin/users"
                selected={location.pathname === '/admin/users'}
              >
                <ListItemIcon>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t.adminUsers.navTitle} />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/admin/pricing"
                selected={location.pathname === '/admin/pricing'}
              >
                <ListItemIcon>
                  <PaidIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t.adminPricing.navTitle} />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/admin/shares"
                selected={location.pathname === '/admin/shares'}
              >
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t.videoShares.navTitle} />
              </ListItemButton>
            </List>
          </>
        )}

        <Box sx={{ px: 1, pb: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
          <ListItemButton onClick={() => void logout()}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t.welcome.logOut} />
          </ListItemButton>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ px: { xs: 3, md: 5 }, py: 2 }}
        >
          <AgentJobsIndicator />
          <NewVersionNotice />
          {credits != null && (
            <Tooltip title="1 credit = $0.01" arrow>
              <Box
                component={NavLink}
                to="/credits"
                sx={{
                  textDecoration: 'none',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.5,
                  fontSize: 13,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                {formatCredits(credits)} {t.credits.balanceUnit}
              </Box>
            </Tooltip>
          )}
          <LocaleSwitcher />
          {user && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {user.name || user.email}
              </Typography>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                size="small"
                sx={{ p: 0 }}
                aria-label="account menu"
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                  {initial}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {accountItems.map((item) => (
                  <MenuItem key={item.to} onClick={() => goTo(item.to)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    void logout();
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t.welcome.logOut} />
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </Stack>

        <Box sx={{ px: { xs: 3, md: 5 }, pb: 6, maxWidth: 1180 }}>
          <PromoBanners />
          {children}
        </Box>
      </Box>
    </Box>
    </SignupNudgeProvider>
  );
}

// Persistent conversion surfaces: a guest pill for anonymous users (their
// always-available path to the signup nudge besides the Generate gate) and a
// dismissible "verify email → credits" banner for eligible signed-up users.
// Must render inside SignupNudgeProvider.
function PromoBanners() {
  const { user } = useAuth();
  const t = useTranslation();
  const navigate = useNavigate();
  const { open } = useSignupNudge();
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(
    () => sessionStorage.getItem('happypony-email-banner-dismissed') === '1'
  );

  if (!user) return null;

  if (user.isAnonymous) {
    return (
      <Alert
        severity="info"
        icon={false}
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small" onClick={() => open('inApp')}>
            {t.emailPromo.guestBannerCta}
          </Button>
        }
      >
        {t.emailPromo.guestBannerText}
      </Alert>
    );
  }

  if (!user.email && !user.emailPromoGranted && !emailBannerDismissed) {
    return (
      <Alert
        severity="info"
        icon={false}
        sx={{ mb: 3 }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button color="inherit" size="small" onClick={() => navigate('/credits')}>
              {t.emailPromo.bannerCta}
            </Button>
            <IconButton
              size="small"
              color="inherit"
              aria-label={t.common.close}
              onClick={() => {
                sessionStorage.setItem('happypony-email-banner-dismissed', '1');
                setEmailBannerDismissed(true);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        {format(t.emailPromo.bannerText, { credits: PROMO_CREDITS.EMAIL_VERIFY })}
      </Alert>
    );
  }

  return null;
}
